import React from "react";
import { Upload, Loader2, Trash2, FileArchive } from "lucide-react";
import { Panel } from "@/components/common/Panel";
import { ConfidenceMeter } from "@/components/common/Badges";
import { TID } from "@/lib/testIds";
import PasteTextPanel from "@/components/common/PasteTextPanel";

const ARTIFACT_TYPE_LABELS = {
    abap_program: "ABAP Program",
    authority_check: "AUTHORITY-CHECK",
    authorization_object: "Authorization Object",
    su24_export: "SU24 Export",
    su22_export: "SU22 Export",
    usobx: "USOBX",
    usobt: "USOBT",
    su53: "SU53 Trace",
    stauthtrace: "STAUTHTRACE",
    odata_metadata: "OData Metadata",
    segw_metadata: "SEGW",
    mpc_ext: "MPC_EXT",
    dpc_ext: "DPC_EXT",
    atc_report: "ATC Report",
    auth_matrix: "Authorization Matrix",
    security_design: "Security Design",
    functional_spec: "Functional Spec",
    technical_spec: "Technical Spec",
    fiori_catalog: "Fiori Catalog",
    transport_doc: "Transport",
    unknown: "Unclassified",
};

export function ArtifactsTab({
    artifacts, uploading, dragOver, setDragOver, handleDrop, fileRef, upload, deleteArtifact, workspaceId, archiveReport = [], onIngested,
}) {
    return (
        <div className="space-y-5">
            <div
                data-testid={TID.workspaceUploadZone}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={`cursor-pointer panel-elevated border-dashed transition-all p-10 text-center ${
                    dragOver ? "border-cyan-400 bg-cyan-500/5" : ""
                }`}
            >
                <input
                    data-testid={TID.workspaceUploadInput}
                    ref={fileRef}
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => upload(Array.from(e.target.files || []))}
                />
                <Upload size={32} className="mx-auto text-cyan-400 mb-3" strokeWidth={1.5} />
                <div className="font-display text-lg text-white">Drop SAP engineering artifacts here</div>
                <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                    ABAP source, SU24/SU22 exports, OData metadata (EDMX), SU53 traces, ATC reports,
                    Excel matrices, PDFs and ZIP archives. Auto-classified on upload.
                </div>
                {uploading && (
                    <div className="mt-4 text-xs text-cyan-300 font-mono flex items-center justify-center gap-2">
                        <Loader2 size={12} className="animate-spin" /> Parsing artifacts…
                    </div>
                )}
            </div>

            {archiveReport.length > 0 && (
                <Panel
                    eyebrow="Archive intake"
                    title={`${archiveReport.length} ZIP archive(s) expanded`}
                >
                    <div className="space-y-3" data-testid={TID.archiveReport}>
                        {archiveReport.map((a) => (
                            <div key={a.filename} className="panel px-3 py-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <FileArchive size={13} className="text-cyan-400" />
                                    <span className="font-mono text-[12px] text-cyan-300/90">{a.filename}</span>
                                    <span className="text-[11px] text-slate-400">{a.members_registered} registered</span>
                                    {a.skipped?.length > 0 && (
                                        <span className="text-[11px] text-amber-400">{a.skipped.length} skipped</span>
                                    )}
                                    {a.truncated && (
                                        <span className="text-[10px] font-mono uppercase tracking-wider text-rose-400">limit reached</span>
                                    )}
                                </div>
                                {a.skipped?.length > 0 && (
                                    <ul className="mt-2 space-y-0.5">
                                        {a.skipped.slice(0, 8).map((s) => (
                                            <li key={s.name} className="text-[11px] text-slate-500 font-mono truncate">
                                                {s.name} — {s.reason}
                                            </li>
                                        ))}
                                        {a.skipped.length > 8 && (
                                            <li className="text-[11px] text-slate-400 font-mono">+{a.skipped.length - 8} more…</li>
                                        )}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </div>
                </Panel>
            )}

            <Panel eyebrow={`${artifacts.length} parsed`} title="Artifacts">
                <table className="tbl">
                    <thead>
                        <tr>
                            <th>Filename</th>
                            <th>Type</th>
                            <th>Classification</th>
                            <th>Size</th>
                            <th>Created</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {artifacts.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-xs text-slate-500 font-mono py-6">No artifacts uploaded yet.</td></tr>
                        )}
                        {artifacts.map((a) => (
                            <tr key={a.id}>
                                <td className="font-mono text-cyan-300/90 text-[12px]">
                                    {a.filename}
                                    {a.source_archive && (
                                        <span className="ml-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-slate-500 border border-white/10 rounded-sm px-1 py-0.5">
                                            <FileArchive size={9} /> zip
                                        </span>
                                    )}
                                </td>
                                <td>{ARTIFACT_TYPE_LABELS[a.artifact_type] || a.artifact_type}</td>
                                <td><ConfidenceMeter value={a.classification_confidence} label="" /></td>
                                <td className="font-mono text-slate-400">{(a.size_bytes / 1024).toFixed(1)} KB</td>
                                <td className="font-mono text-slate-500 text-[11px]">{new Date(a.created_at).toLocaleString()}</td>
                                <td>
                                    <button
                                        data-testid={TID.artifactDeleteBtn(a.id)}
                                        onClick={() => deleteArtifact(a)}
                                        className="text-slate-500 hover:text-rose-400 transition-colors"
                                        aria-label="Remove artifact"
                                        title="Remove artifact from workspace"
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Panel>

            <PasteTextPanel workspaceId={workspaceId} onIngested={onIngested} />
        </div>
    );
}
